
import React from "react";
import InstructionCheckbox from "./InstructionCheckbox";
import ImageWithFallback from "../ImageWithFallback";
import { Separator } from "@/components/ui/separator";

interface CrealityInstructionsProps {
  currentPass: number;
  newFlowRatio?: string;
  checkedSteps: Record<string, boolean>;
  onStepCheck: (stepId: string, checked: boolean) => void;
  onCheckAll?: () => void;
  previousFlowRatioOriginal?: string;
}

const CrealityInstructions = ({
  currentPass,
  newFlowRatio,
  checkedSteps,
  onStepCheck,
  onCheckAll,
  previousFlowRatioOriginal
}: CrealityInstructionsProps) => {
  // Get current theme from document
  const getCurrentTheme = () => {
    if (typeof document !== 'undefined') {
      return document.documentElement.getAttribute('data-theme') || 'light';
    }
    return 'light';
  };

  const theme = getCurrentTheme();
  
  const dropdownIcon = theme === "dark" 
    ? "/lovable-uploads/4b6c3e0e-5356-4046-931a-c1131e10ead9.png"
    : "/lovable-uploads/78941090-a5a7-4a41-969f-15cc7b0eba5e.png";

  // For Pass 2, display the calculated flow ratio with proper formatting
  const getDisplayFlowRatio = () => {
    if (currentPass === 2 && newFlowRatio) {
      return newFlowRatio;
    }
    return "";
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-3">
          <div className="flex items-center justify-start gap-2 flex-wrap">
            <h4 className="font-semibold text-lg text-gray-900 dark:text-white">Creality Print Guide</h4>
            <span className={`px-2 py-1 rounded-md text-xs font-semibold flex-shrink-0 ${currentPass === 1 ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800"}`}>
              Pass {currentPass}
            </span>
          </div>
        </div>
      </div>
      
      <ol className="space-y-3">
        {currentPass === 2 ? <>
            <InstructionCheckbox id="creality-step1-pass2" checked={checkedSteps["creality-step1-pass2"] || false} onCheckedChange={checked => onStepCheck("creality-step1-pass2", checked)}>
              <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Save</span> your new flow ratio <span className="text-gray-900 dark:text-white">(</span><span className="text-green-600 dark:text-green-400 font-semibold">{getDisplayFlowRatio()}</span><span className="text-gray-900 dark:text-white">)</span> in the <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>filament settings</span>
            </InstructionCheckbox>
            <Separator className="my-3 bg-gray-500" />
            <InstructionCheckbox id="creality-step2-pass2" checked={checkedSteps["creality-step2-pass2"] || false} onCheckedChange={checked => onStepCheck("creality-step2-pass2", checked)}>
              Click <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>File</span> {"> "} <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>New project</span> to reset the slicer settings
            </InstructionCheckbox>
            <Separator className="my-3 bg-gray-500" />
            <InstructionCheckbox id="creality-step3-pass2" checked={checkedSteps["creality-step3-pass2"] || false} onCheckedChange={checked => onStepCheck("creality-step3-pass2", checked)}>
              <span>
                Click the{" "}
                <ImageWithFallback src={dropdownIcon} alt="dropdown arrow" className="h-4 w-4 inline mx-1" />
                <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>dropdown arrow</span> in the <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>top left corner</span> of Creality Print, next to "<span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>File</span>"
              </span>
            </InstructionCheckbox>
            <Separator className="my-3 bg-gray-500" />
            <InstructionCheckbox id="creality-step4-pass2" checked={checkedSteps["creality-step4-pass2"] || false} onCheckedChange={checked => onStepCheck("creality-step4-pass2", checked)}>
              Click <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Calibration</span> → <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Flow Rate (Pass 2)</span>
            </InstructionCheckbox>
            <Separator className="my-3 bg-gray-500" />
            <InstructionCheckbox id="creality-step5-pass2" checked={checkedSteps["creality-step5-pass2"] || false} onCheckedChange={checked => onStepCheck("creality-step5-pass2", checked)}>
              <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Slice</span> and <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>print</span> the calibration objects
            </InstructionCheckbox>
          </> : <>
            <InstructionCheckbox id="creality-step1" checked={checkedSteps["creality-step1"] || false} onCheckedChange={checked => onStepCheck("creality-step1", checked)}>
              <span>
                Click the{" "}
                <ImageWithFallback src={dropdownIcon} alt="dropdown arrow" className="h-4 w-4 inline mx-1" />
                <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>dropdown arrow</span> in the <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>top left corner</span> of Creality Print, next to "<span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>File</span>"
              </span>
            </InstructionCheckbox>
            <Separator className="my-3 bg-gray-300" />
            <InstructionCheckbox id="creality-step2" checked={checkedSteps["creality-step2"] || false} onCheckedChange={checked => onStepCheck("creality-step2", checked)}>
              Click <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Calibration</span> → <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Flow Rate (Pass {currentPass})</span>
            </InstructionCheckbox>
            <Separator className="my-3 bg-gray-300" />
            <InstructionCheckbox id="creality-step4" checked={checkedSteps["creality-step4"] || false} onCheckedChange={checked => onStepCheck("creality-step4", checked)}>
              <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Slice</span> and <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>print</span> the calibration objects
            </InstructionCheckbox>
          </>}
      </ol>

      <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
        <div className={`text-sm space-y-1 ${theme === 'dark' ? 'text-blue-200' : 'text-blue-700'}`}>
          {currentPass === 1 && (
            <p>Tip: You can usually get away with removing the highest and lowest values to save time and filament.</p>
          )}
          <p>Check all boxes to continue to the next step.</p>
        </div>
      </div>
    </div>
  );
};

export default CrealityInstructions;
